import { RemediationDecision } from '@libs/constants';

/** The shape both prose and spreadsheet changes share for cursor purposes. */
interface DecidableChange {
  changeId: string;
  decision: RemediationDecision;
}

/**
 * Where the cursor goes after a change is decided.
 *
 * Reviewing is a queue, not a browse: the reviewer's next question after
 * accepting change 3 is always "what about change 4". Leaving the cursor put
 * meant reaching for the mouse between every decision, which on a document with
 * twenty changes is twenty interruptions to a task that is otherwise pure
 * keyboard.
 *
 * The change just decided is excluded explicitly rather than filtered by
 * decision, because this runs against the array the component already holds —
 * the mutation has not refetched yet, so that change still reads as PENDING.
 *
 * Order of preference:
 *  1. the next still-pending change after this one — the queue, in order;
 *  2. the first still-pending change before it, wrapping once, so a reviewer who
 *     jumped into the middle still gets swept back through what they skipped;
 *  3. nothing. Every other change is decided, and moving to an already-decided
 *     one would imply there is more to do. The caller keeps the cursor where it
 *     is so the reviewer can see what they just chose.
 */
export function nextChangeAfterDecision(
  changes: readonly DecidableChange[],
  decidedId: string,
): string | null {
  const index = changes.findIndex((c) => c.changeId === decidedId);
  if (index === -1) return null;

  const isPending = (c: DecidableChange) =>
    c.changeId !== decidedId && c.decision === RemediationDecision.PENDING;

  const after = changes.slice(index + 1).find(isPending);
  if (after) return after.changeId;

  const before = changes.slice(0, index).find(isPending);
  return before?.changeId ?? null;
}

/** A document in the review picker, as far as the cursor is concerned. */
interface ReviewableDoc {
  itemId: string;
}

/**
 * The next document to open once the current one has nothing left to decide.
 *
 * Only meaningful when the reviewer has actually finished a document — a
 * partially decided document must never be navigated away from, because the
 * reviewer would lose their place in it. The caller owns that check;
 * this only answers "which document is next".
 *
 * Forward from the current position, then wrapping, so the last document in the
 * list hands back to the first one still outstanding rather than dead-ending.
 */
export function nextDocumentAfter(
  docs: readonly ReviewableDoc[],
  currentItemId: string,
  hasOutstandingWork: (itemId: string) => boolean,
): string | null {
  const index = docs.findIndex((d) => d.itemId === currentItemId);
  if (index === -1) return null;

  const ordered = [...docs.slice(index + 1), ...docs.slice(0, index)];
  return ordered.find((d) => hasOutstandingWork(d.itemId))?.itemId ?? null;
}
